// CSS
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

// utilities
import * as Utils from './utils.js';

// React and Bootstrap
import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import InputGroup from 'react-bootstrap/InputGroup';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Toast from 'react-bootstrap/Toast';
import Modal from 'react-bootstrap/Modal';
import ButtonGroup from 'react-bootstrap/ButtonGroup';

// CodeMirror
import {Controlled as CodeMirror} from 'react-codemirror2';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/material.css';
import 'codemirror/addon/lint/lint.js';
import 'codemirror/addon/lint/lint.css';
import 'codemirror/addon/lint/yaml-lint.js';
import 'codemirror/addon/lint/json-lint.js';
import 'codemirror/addon/lint/html-lint.js';
import 'codemirror/mode/yaml/yaml.js';
import 'codemirror/mode/javascript/javascript.js'; // also for json
import 'codemirror/mode/xml/xml.js';
import 'codemirror/mode/htmlmixed/htmlmixed.js';

// linting and formatting
import HTMLHint from 'htmlhint';
import HTMLCleaner from 'clean-html';
import jsyaml from 'js-yaml/lib/js-yaml.js';
import {schema as yamlCfnSchema} from 'yaml-cfn/index.js';
var jsonlint = require("jsonlint-mod");
var vkbeautify = require("vkbeautify");

const cm = require('codemirror');

/*
### Overview ###

This App is constructed around the CodeMirror editor, which offers:
 - syntax highlight
 - syntax linter

Syntax highlight is a native editor functionality, configured by the editor "mode" option.
Syntax linter is an addon, which works with editor "helpers" functions. To modify linting logic
for a specific language overwrite its helper.

The syntax linting is repeated by the App itself to show errors in the box below the editor.
Note: this means the linting process is repeated two times for each user input: one time by
      CodeMirror, one time by the App. This could be avoided overwritting the linting helpers.

When user paste a content in the editor, and the editor was previously empty, the App try to guess
which language the user pasted.

### Class structure ###

The App class is composed of:
 - a constructor
 - a render method
 - some "event" methods (on<Event> style) which integrate business logic
 - a list of language objects, which contains language specific logic

### How to add a new language ###

To add a new language:
 - write the corresponding object (see other objects as reference)
 - add the object to this.languages list (populated in constructor)
 - add the object to this.autodetectLanguages list (populated in constructor)

 */

export default class App extends React.Component {

	constructor(props) {
	  super(props);

		// bind this class functions to "this", to be used as event callbacks
		this.onClickFormat = this.onClickFormat.bind(this);
	  	this.onChangeEditor = this.onChangeEditor.bind(this);
		this.onBeforeChangeEditor = this.onBeforeChangeEditor.bind(this);
		this.onClickLanguage = this.onClickLanguage.bind(this);
		this.onClickMinify = this.onClickMinify.bind(this);
		this.onClickExample = this.onClickExample.bind(this);
		this.autodetectLanguage = this.autodetectLanguage.bind(this);
		this.onClickCopy = this.onClickCopy.bind(this);
		this.onClickAbout = this.onClickAbout.bind(this);
		this.onLanguageOptionsChange = this.onLanguageOptionsChange.bind(this);
		this.onEditorDidMount = this.onEditorDidMount.bind(this);
		this.onWindowResize = this.onWindowResize.bind(this);

		// supported languages, add every new language to this list
		this.languages = [langYaml, langJson, langXml, langHtml];

		// list of autodetected languages, order matters! Add every new language to this list
		this.autodetectLanguages = [langHtml, langXml, langJson, langYaml];

		// overwrite default CodeMirror linter for YAML
		cm.registerHelper("lint", "yaml",
			(text) => langYaml.linterHelper(text, this.state.languageOptions));

		// overwrite default CodeMirror linter for JSON
		cm.registerHelper("lint", "json", 
			(text) => langJson.linterHelper(text, this.state.languageOptions));

		// set initial state
	  	this.state = {
			value: "", // current content in the editor, updated on every user input
			errors: [], // current linting errors, updated for every user input
			language: langYaml, // object for the currently selected language, contains language specific logic
			showToastFormat: false, // set to true to show a "format error" toast
			showToastCopy: false, // set to true to show a "copied to clipboard" toast
			showDialogAbout: false, // set to true to show the About dialog window
			showDialogExample: false, // set to true to show the Example dialog window
			autodetect: true // set to false to disable language auto detection
		};
	}

	/* This method tries to detect the input language */
	autodetectLanguage(input) {

		// detect XML specific header
		if (input.startsWith("<?xml") || input.startsWith("<?XML")) {
			if (langXml.lint(input, this.state.languageOptions).length === 0) {
				return this.languages.find( (l) => l.mimeType === "application/xml" );
			}
		}

		// detect HTML specific header
		if (input.startsWith("<!DOCTYPE html>") || input.startsWith("<html")) {
			if (langHtml.lint(input, this.state.languageOptions).length === 0) {
				return this.languages.find( (l) => l.mimeType === "text/html" );
			}
		}

		// the first linter that doesn't emit errors is a good choice
		return this.languages.find((lang) => lang.lint(input, this.state.languageOptions).length === 0);
	}


	/* COMPONENT LIFECYCLE */

  componentDidMount() {
		this.onWindowResize();
    window.addEventListener("resize", this.onWindowResize);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.onWindowResize);
  }

	/* CodeMirror textarea must be resized programmatically */
	onWindowResize() {
		// 190px is enough space for the error box
		this.setState({editorHeight: window.innerHeight - 190});
	}


	/* CODEMIRROR CALLBACKS */

	onEditorDidMount(editor) {
		this.editor = editor;
	}

	onBeforeChangeEditor(editor, data, value) {

		// autodetect language
		if (this.state.autodetect && this.state.value.length === 0 && value.length > 1) {
			let newLanguage = this.autodetectLanguage(value);
			if (newLanguage) {
				this.setState({language: newLanguage});
			}
		}

		this.setState({value, autodetect: false});
	}

	onChangeEditor(editor, data, value) {
		this.setState({
			value: value,
			errors: this.state.language.lint(value, this.state.languageOptions)
		});
	}


	/* BUTTONS AND DROPDOWNS CALLBACKS */

	onClickFormat() {
		try {
			this.setState({
				value: this.state.language.format(this.state.value,
					this.state.formatOptions, this.state.languageOptions)
			});
		} catch (e) {
			console.log(e);
			this.setState({showToastFormat: true});
		}
	}

	onClickLanguage(language) {
		this.setState((prev, props) => { return {
				language,
				errors: language.lint(prev.value, this.state.languageOptions),
				autodetect: false
		}});
	}

	onClickMinify() {
		try {
			this.setState({
				value: this.state.language.minify(this.state.value,
					this.state.formatOptions, this.state.languageOptions)
			});
		} catch (e) {
			console.log(e);
			this.setState({showToastFormat: true});
		}
	}

	onClickExample() {
		if (this.state.value.length > 0) {
			this.setState({showDialogExample: true});
		} else {
			this.setState({
				value: this.state.language.format(this.state.language.example,
					this.state.formatOptions, this.state.languageOptions)
			});
		}
	}

	onClickCopy() {
		navigator.clipboard.writeText(this.state.value);
		this.setState({showToastCopy: true});
	}

	onClickAbout() {
		this.setState({showDialogAbout: true});
	}

	onLanguageOptionsChange(languageOptions) {
		this.setState({
			languageOptions,
			errors: this.state.language.lint(this.state.value, languageOptions)
		}, () => { if (this.editor) this.editor.performLint() } );
	}

	render() {

		// build languages dropdown
		let languagesDropdown = this.languages.map(language => (
			<Dropdown.Item key={language.displayName} onClick={() => this.onClickLanguage(language)}>
				{language.displayName}
			</Dropdown.Item>
		));

		// build error list
		let errors = this.state.errors.map((error) => <div key={error}>{error}</div>);

		return <div id="container">

			{/* BUTTON TOOLBAR */}
			<Row>

				{/* LANGUAGES DROPDOWN */}
				<Col md="auto">
					<Dropdown>
						<Dropdown.Toggle size="sm" variant="primary" id="dropdown-basic" style={{width: "100px"}}>
							{this.state.language.displayName}
						</Dropdown.Toggle>
						<Dropdown.Menu>{languagesDropdown}</Dropdown.Menu>
					</Dropdown>
				</Col>

				{/* LANGUAGE SPECIFIC OPTIONS */}
				<Col md="auto">
					{ this.state.language.languageOptionsComponent &&
						<this.state.language.languageOptionsComponent onChange={this.onLanguageOptionsChange} /> }
				</Col>

				<Col md="auto"><div className="verticalSeparator" /></Col>

				{/* FORMAT AND MINIFY BUTTONS AND LANGUAGE SPECIFI OPTIONS */}
				<Col md="auto">
					<ButtonGroup aria-label="Basic example">
						<Button size="sm" variant="secondary" onClick={this.onClickFormat}
							style={{width: "75px"}}>Format</Button>
						<Button size="sm" variant="secondary" onClick={this.onClickMinify}
							style={{borderLeft: "1px solid white", width: "80px"}}>Minify</Button>
					</ButtonGroup>
				</Col>
				<Col md="auto">
					<this.state.language.formatOptionsComponent
						onChange={(formatOptions) => this.setState({formatOptions})} />
				</Col>

				<Col></Col> {/* SPACING */}

				{/* OTHER BUTTONS */}
				<Col md="auto">
					<Button size="sm" variant="secondary" onClick={this.onClickExample} >Example</Button>
				</Col>
				<Col md="auto">
					<Button size="sm" variant="secondary" onClick={this.onClickCopy} >Copy to clipboard</Button>
				</Col>
				<Col md="auto">
					<Button size="sm" variant="secondary" onClick={this.onClickAbout} >About</Button>
				</Col>
			</Row>

			<div style={{height: "10px"}} />

			{/* OVERWRITE CODEMIRROR HEIGHT TO PERMIT DYNAMIC HEIGHT */}
			<style dangerouslySetInnerHTML={{
				__html: '.CodeMirror {height: ' + this.state.editorHeight + 'px !important}'
			}} />

			{/* THE ACTUAL EDITOR */}
			<CodeMirror value={this.state.value}
				onChange={this.onChangeEditor}
				onBeforeChange={this.onBeforeChangeEditor}
				editorDidMount={this.onEditorDidMount}
			  options={{
				      mode: this.state.language.mimeType,
				      autofocus: true,
				      lineNumbers: true,
							lineWrapping: true,
							autoCloseBrackets: true,
							matchBrackets: true,
              gutters: ["CodeMirror-lint-markers"],
              lint: true
			  }}
			/>

			{/* ERROR / VALID BAR */}
			<div style={{height: "10px"}} />
			{!errors.length && <Alert variant="info">Valid {this.state.language.displayName}</Alert>}
			{errors.length > 0 && <Alert variant="danger">{errors}</Alert>}

			{/* ABOUT MODAL */}
			<Modal show={this.state.showDialogAbout} onHide={() => this.setState({showDialogAbout: false})}>
        <Modal.Header closeButton>
          <Modal.Title>About</Modal.Title>
        </Modal.Header>
        <Modal.Body>
					<p>
						Total Formatter is an online tool to format, minify and validate markup languages. It currently supports&nbsp;
						<a target="_blank" href="https://en.wikipedia.org/wiki/YAML">YAML</a>,&nbsp;
						<a target="_blank" href="https://en.wikipedia.org/wiki/JSON">JSON</a>,&nbsp;
						<a target="_blank" href="https://en.wikipedia.org/wiki/XML">XML</a> and&nbsp;
						<a target="_blank" href="https://en.wikipedia.org/wiki/HTML">HTML</a>.
					</p><p>
						All text written in the editor will not be sent to the backend.
						All operations are performed in the browser using Javascript.
					</p>
				</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => this.setState({showDialogAbout: false})}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

			{/* EXAMPLE CONFIRMATION MODAL */}
			<Modal show={this.state.showDialogExample} onHide={() => this.setState({showDialogExample: false})}>
        <Modal.Body>
					This action will delete current editor content and replace it with an example text.<br/>
					<br/>
					Do you want to continue?
				</Modal.Body>
        <Modal.Footer>
					<Button variant="secondary" onClick={() => this.setState({showDialogExample: false})}>
            No
          </Button>
					<Button variant="danger" onClick={() => {
						this.setState({
							showDialogExample: false,
							value: this.state.language.format(this.state.language.example,
								this.state.formatOptions, this.state.languageOptions)
						});
					}}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>

			{/* "FORMAT ERROR" TOAST */}
			<Toast show={this.state.showToastFormat} autohide delay="3000" style={{top: "50%"}}
					onClose={() => this.setState({showToastFormat:false})}>
				<Toast.Body style={{backgroundColor: "#E17474", color: "white"}}>
					Cannot format invalid {this.state.language.displayName}!
				</Toast.Body>
			</Toast>

			{/* "COPY TO CLIPBOARD" TOAST */}
			<Toast show={this.state.showToastCopy} autohide delay="1000" style={{top: "45%"}}
					onClose={() => this.setState({showToastCopy:false})}>
				<Toast.Body style={{backgroundColor: "#7DCEA0", color: "black"}}>
					Text copied to clipboard
				</Toast.Body>
			</Toast>

		</div>
	}
}


/*
From this point on there are langauge specific objects. They are used to incapsulate language
specific logic. Each language specific object must provide:

- displayName: (string) name to be displayed on UI (currently used in the language dropdown)
- mimeType: (string) language mime type, used to set CodeMirror linter
- linterHelper: (function) linter helper to overwrite CodeMirror default
- minify: (function) used to minify code (minify button)
- format: (function) used to format code (format button)
- lint: (function) used to lint code (used for the lint box under the editor)
- languageOptionsComponent: (function) a React component class for the specific language options UI
														it must accept a "onChange" prop function to notify option changes
- formatOptionsComponent: (function) a React component class for the specific language format
													options it must accept a "onChange" prop function to notify option changes
- example: (string) example used by the example button
*/

/*****************/
/* YAML LANGUAGE */
/*****************/

let langYaml = {

    displayName: "YAML",
	mimeType: "text/yaml",

	linterHelper: (text, languageOptions) => {
	  var found = [];
	  try {
			if (languageOptions) {
				jsyaml.loadAll(text, null, languageOptions);
			} else {
				jsyaml.safeLoadAll(text);
			}
		} catch(e) {
	      var loc = e.mark,
	          // js-yaml YAMLException doesn't always provide an accurate lineno
	          // e.g., when there are multiple yaml docs
	          // ---
	          // ---
	          // foo:bar
	          from = loc ? cm.Pos(loc.line, loc.column) : cm.Pos(0, 0),
	          to = from;
	      found.push({ from: from, to: to, message: e.message });
	  }
	  return found;
	},

	minify: (input, formatOptions, languageOptions) => {

  	let value = "";

  	// find directives if any
  	let lines = input.split("\n");
  	for (let idx = 0; idx < lines.length && lines[idx].startsWith("%"); idx++) {
  		value += lines[idx] + "\n";
  	}

  	// separate directives from documents
  	if (value.length > 0) {
  		value += "---\n";
  	}

  	// copy formatted documents
  	let docs = jsyaml.loadAll(input, null, languageOptions);
  	value += docs.map((d) => jsyaml.dump(d, { flowLevel: 0, schema: languageOptions.schema }));

		return value;
  },

  format: (input, formatOptions, languageOptions) => {

  	let value = "";

  	// find directives if any
  	let lines = input.split("\n");
  	for (let idx = 0; idx < lines.length && lines[idx].startsWith("%"); idx++) {
  		value += lines[idx] + "\n";
  	}

  	// separate directives from documents
  	if (value.length > 0) {
  		value += "---\n";
  	}

  	// parse documents
		let docs = jsyaml.loadAll(input, null, languageOptions);

		// write formatted documents
		return docs.map((d) =>
				jsyaml.dump(d, Utils.extend({schema: languageOptions.schema}, formatOptions)))
			.join("---\n");
  },

  lint: (input, languageOptions) => {

    try {
      jsyaml.loadAll(input, null, languageOptions);
      return [];
    } catch (e) {
      return [e.message];
    }
  },

	languageOptionsComponent: class extends React.Component {

				constructor(props) {
					super(props);
					yamlCfnSchema.displayName = "AWS CloudFormation";
					jsyaml.CORE_SCHEMA.displayName = "Default schema";

					this.change = this.change.bind(this);
					this.state = {
						schema: jsyaml.CORE_SCHEMA
					};
					this.props.onChange(this.state);
				}

				change(newState) {
					this.setState(newState, () => this.props.onChange(this.state));
				}

				render() {
					return <Row>
						<Col md="auto">
							<Dropdown>
								<Dropdown.Toggle size="sm" variant="secondary" id="dropdown-basic">
									{this.state.schema.displayName || this.state.schema}
								</Dropdown.Toggle>
								<Dropdown.Menu>
									<Dropdown.Item key="core"
											onClick={() => this.change({schema: jsyaml.CORE_SCHEMA})}>
										Default schema
									</Dropdown.Item>
									<Dropdown.Item key="cfn"
											onClick={() => this.change({schema: yamlCfnSchema})}>
										AWS CloudFormation
									</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>
						</Col>
					</Row>
				}
	},

	formatOptionsComponent: class extends React.Component {

		constructor(props) {
			super(props);
			this.change = this.change.bind(this);
			this.state = {
				indent: 4,
				lineWidth: 80,
				sortKeys: false,
				noArrayIndent: false
			};
			this.props.onChange(this.state);
		}

		change(newState) {
			this.setState(newState, () => this.props.onChange(this.state));
		}

		render() {

			return <Row>
				<Col md="auto">
					<InputGroup size="sm">
				    <InputGroup.Prepend><InputGroup.Text>Indent</InputGroup.Text></InputGroup.Prepend>
				    <Form.Control type="number" value={this.state.indent} min="0" max="50" style={{width: "60px"}}
							onChange={(e) => this.change({indent: e.target.value}) } />
				  </InputGroup>
				</Col>
				<Col md="auto">
					<InputGroup size="sm">
						<InputGroup.Prepend><InputGroup.Text>Line width</InputGroup.Text></InputGroup.Prepend>
						<Form.Control type="number"  value={this.state.lineWidth} style={{width: "60px"}}
							onChange={(e) => this.change({lineWidth: e.target.value}) } />
					</InputGroup>
				</Col>
				<Col md="auto">
					<Form.Check size="sm" type="checkbox" checked={this.state.sortKeys} style={{height: "100%"}}
						label="Sort keys"
						onChange={(e) => this.change({sortKeys: e.target.checked}) } />
				</Col>
				<Col md="auto">
			    <Form.Check size="sm" type="checkbox" checked={!this.state.noArrayIndent}
						label="Indent lists"
						onChange={(e) => this.change({noArrayIndent: !e.target.checked}) } />
				</Col>
			</Row>
		}
	},

	example: "%YAML 1.2\n---\n{YAML: YAML Ain't Markup Language, What It Is: YAML is a human friendly data serialization standard for all programming languages., YAML Resources: {YAML 1.2 (3rd Edition): 'http://yaml.org/spec/1.2/spec.html', YAML 1.1 (2nd Edition): 'http://yaml.org/spec/1.1/', YAML 1.0 (1st Edition): 'http://yaml.org/spec/1.0/', YAML Issues Page: 'https://github.com/yaml/yaml/issues', YAML Mailing List: yaml-core@lists.sourceforge.net, YAML IRC Channel: '#yaml on irc.freenode.net', YAML Cookbook (Ruby): 'http://yaml4r.sourceforge.net/cookbook/ (local)', YAML Reference Parser: 'http://ben-kiki.org/ypaste/', YAML Test Suite: 'https://github.com/yaml/yaml-test-suite'}, Projects: {C/C++ Libraries: [libfyaml, libyaml, libcyaml, Syck, yaml-cpp], Crystal: [YAML], Ruby: [psych, RbYaml, yaml4r], Python: [PyYAML, ruamel.yaml, PySyck], Java: [JvYaml, SnakeYAML, YamlBeans, JYaml, Camel], Perl Modules: [YAML, 'YAML::XS', 'YAML::Syck', 'YAML::Tiny', 'YAML::PP', PlYaml], 'C#/.NET': [YamlDotNet, yaml-net, yatools.net], Golang: [Go-yaml, Go-gypsy], PHP: [The Yaml Component, php-yaml, syck, spyc], OCaml: [ocaml-syck], Javascript: [JS-YAML, JS-YAML Online], Actionscript: [as3yaml], Haskell: [HsYAML, YamlReference, yaml], Dart: [yaml], Rust: [yaml-rust, serde-yaml], Nim: [NimYAML], Delphi: [Neslib.Yaml], Others: [yamlvim (src)]}, Related Projects: [Rx, Kwalify, yaml_vim, yatools.net, JSON, Pygments]}"

}

/*****************/
/* JSON LANGUAGE */
/*****************/

let langJson = {

	displayName: "JSON",
	mimeType: "application/json",

	linterHelper: (text, languageOptions) => {
		var found = [];
		jsonlint.parseError = function(str, hash) {
		  var loc = hash.loc;
		  found.push({from: CodeMirror.Pos(loc.first_line - 1, loc.first_column),
					  to: CodeMirror.Pos(loc.last_line - 1, loc.last_column),
					  message: str});
		};

		try { 
			jsonlint.parse(text); 
		} catch(e) {}

		return found;
	},

	minify: (input) => {
		return JSON.stringify(JSON.parse(input));
	},

	format: (input, options) => {
		return JSON.stringify(JSON.parse(input), null, options.indent ? parseInt(options.indent) : 4);
	},

	lint: (input) => {
		try {
			jsonlint.parse(input);
			return [];
		} catch (e) {
			return [e.message];
		}
	},

	formatOptionsComponent: class extends React.Component {

		constructor(props) {
			super(props);
			this.change = this.change.bind(this);
			this.state = {
				indent: 4
			};
			this.props.onChange(this.state);
		}

		change(newState) {
			this.setState(newState, () => this.props.onChange(this.state));
		}

		render() {
			return <Row>
				<Col md="auto">
					<InputGroup size="sm">
				    <InputGroup.Prepend><InputGroup.Text>Indent</InputGroup.Text></InputGroup.Prepend>
				    <Form.Control type="number" value={this.state.indent} min="0" max="50" style={{width: "80px"}}
							onChange={(e) => this.change({indent: e.target.value}) } />
				  </InputGroup>
				</Col>
			</Row>
		}
	},

	example: '{"glossary":{"title":"example glossary","GlossDiv":{"title":"S","GlossList":{"GlossEntry":{"ID":"SGML","SortAs":"SGML","GlossTerm":"Standard Generalized Markup Language","Acronym":"SGML","Abbrev":"ISO 8879:1986","GlossDef":{"para":"A meta-markup language, used to create markup languages such as DocBook.","GlossSeeAlso":["GML","XML"]},"GlossSee":"markup"}}}}}'

}

/****************/
/* XML LANGUAGE */
/****************/

let langXml = {

	displayName: "XML",
	mimeType: "application/xml",

	minify: (input, options) => {
		return vkbeautify.xmlmin(input, options.preserveComments);
	},

	format: function (input, options) {

		let errors = this.lint(input);
		if (errors.length > 0) throw errors;

		return vkbeautify.xml(input, options.indent);
	},

	lint: (input) => {

		const theDom = new window.DOMParser().parseFromString(input, 'application/xml');
		const errors = theDom.getElementsByTagName('parsererror');

		if (errors.length > 0) {

			let returnedErrors = [];
			for (let i=0; i < errors.length; i++) {
				returnedErrors.push(errors[i].getElementsByTagName('div')[0].innerHTML);
			}

			return returnedErrors;
		} else {
			return [];
		}
	},

	formatOptionsComponent: class extends React.Component {

		constructor(props) {
			super(props);
			this.change = this.change.bind(this);
			this.state = {
				indent: 4,
				preserveComments: true
			};
			this.props.onChange(this.state);
		}

		change(newState) {
			this.setState(newState, () => this.props.onChange(this.state));
		}

		render() {
			return <Row>
				<Col md="auto">
					<InputGroup size="sm">
				    <InputGroup.Prepend><InputGroup.Text>Indent</InputGroup.Text></InputGroup.Prepend>
				    <Form.Control type="number" value={this.state.indent} min="0" max="50" style={{width: "80px"}}
							onChange={(e) => this.change({indent: e.target.value}) } />
				  </InputGroup>
				</Col>
				<Col md="auto">
			    <Form.Check size="sm" type="checkbox" checked={this.state.preserveComments}
						label="Preserve Comments (minify)"
						onChange={(e) => this.change({preserveComments: e.target.checked}) } />
				</Col>
			</Row>
		}
	},

	example: '<?xml version="1.0" encoding="UTF-8"?><root><answer>42</answer></root>'

}


/*****************/
/* HTML LANGUAGE */
/*****************/

let langHtml = {

	displayName: "HTML",
	mimeType: "text/html",

	minify: (input, options) => {
		return vkbeautify.xmlmin(input, options.preserveComments);
	},

	format: function (input, formatOptions, languageOptions) {

		console.log(formatOptions);

		let errors = this.lint(input);
		if (errors.length > 0) throw errors;

		// normalize options
		let options = Utils.extend({}, formatOptions);
		if (options.indent) {
			options.indent = ' '.repeat(options.indent);
		}

		let result = "";
		HTMLCleaner.clean(input, options, (html) => result = html);
		return result;
	},

	lint: (input) => {

		let defaultRules = {
	    "tagname-lowercase": true,
	    "attr-lowercase": true,
	    "attr-value-double-quotes": true,
	    "doctype-first": false,
	    "tag-pair": true,
	    "spec-char-escape": true,
	    "id-unique": true,
	    "src-not-empty": true,
	    "attr-no-duplication": true
	  };

    let messages = HTMLHint.verify(input, defaultRules);
		return messages.map((m) => m.message);
	},

	formatOptionsComponent: class extends React.Component {

		constructor(props) {
			super(props);
			this.change = this.change.bind(this);
			this.state = {
				indent: 4,
				wrap: 120,
				preserveComments: true,
				'remove-comments': false,
				'replace-nbsp': false
			};
			this.props.onChange(this.state);
		}

		change(newState) {
			this.setState(newState, () => this.props.onChange(this.state));
		}

		render() {
			return <Row>
				<Col md="auto">
					<InputGroup size="sm">
				    <InputGroup.Prepend><InputGroup.Text>Indent</InputGroup.Text></InputGroup.Prepend>
				    <Form.Control type="number" min="1" max="50" style={{width: "70px"}}
							value={this.state.indent}
							onChange={(e) => this.change({indent: e.target.value}) } />
				  </InputGroup>
				</Col>
				<Col md="auto">
					<InputGroup size="sm">
				    <InputGroup.Prepend><InputGroup.Text>Line width</InputGroup.Text></InputGroup.Prepend>
				    <Form.Control type="number" min="1" max="120" style={{width: "80px"}}
							value={this.state.wrap}
							onChange={(e) => this.change({wrap: e.target.value}) } />
				  </InputGroup>
				</Col>
				<Col md="auto">
			    <Form.Check size="sm" type="checkbox" label="Remove comments"
						checked={this.state['remove-comments']}
						onChange={(e) => this.change({
							'remove-comments': e.target.checked,
							preserveComments: !e.target.checked
						}) } />
				</Col>
				<Col md="auto">
			    <Form.Check size="sm" type="checkbox" label="Replaces &amp;nbsp; with spaces"
						checked={this.state['replace-nbsp']}
						onChange={(e) => this.change({'replace-nbsp': e.target.checked}) } />
				</Col>
			</Row>
		}
	},

	example: '<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Title of the document</title></head><body>Content of the document......</body></html>'

}
